import { Link } from "react-router-dom";
import { ShieldCheck, Cctv, Dog, Network, CheckCircle2, ArrowRight } from "lucide-react";
import { SERVICES } from "../lib/site";
import { CTABand } from "../components/CTABand";
import { useSEO } from "../lib/useSEO";

const ICONS = { ShieldCheck, Cctv, Dog, Network };

const heroImages = {
  "manned-guarding": "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80",
  "cctv": "https://images.unsplash.com/photo-1696133937088-7925539ed9a2?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
  "k9-patrols": "https://images.unsplash.com/photo-1568572933382-74d440642117?auto=format&fit=crop&w=1400&q=80",
  "security-systems": "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80",
};

export default function ServicesPage() {
  useSEO({
    title: "Security Services — Manned Guarding, CCTV, K9 Patrols, Security Systems",
    description: "Full-service UK security: SIA-licensed manned guarding, CCTV tower hire, K9 patrols and integrated security systems. Tailored solutions for construction, substations, industrial and event sites across the UK.",
    keywords: "manned guarding services UK, security guards UK, SIA licensed officers, CCTV tower hire UK, mobile CCTV towers, K9 patrol service, dog handler security, integrated security systems, access control UK, intruder alarm monitoring",
    path: "/services",
  });
  return (
    <div data-testid="services-page" className="pt-24">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(6,182,212,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">
            ◆ Capabilities
          </p>
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tighter max-w-4xl">
            Integrated security<br />
            <span className="text-gradient-cyan">built around your site.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl leading-relaxed">
            Four core capabilities. Engineered to operate as one. From the officer at the gate to the analyst in the control room — every layer is briefed, trained and accountable.
          </p>
        </div>
      </section>

      {/* Services list */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 space-y-20">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          const reverse = i % 2 === 1;
          return (
            <div key={s.id} id={s.id} data-testid={`service-${s.id}`} className="grid lg:grid-cols-12 gap-10 items-center">
              <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]">
                  <img src={heroImages[s.id]} alt={s.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-ink-950/80 backdrop-blur border border-cyan-400/30">
                      <Icon className="w-4 h-4 text-cyan-400" />
                      <span className="font-mono text-[10px] uppercase tracking-wider text-cyan-300">{s.short}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-3">
                  0{i + 1} · Capability
                </p>
                <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">
                  {s.name}
                </h2>
                <p className="text-cyan-300 text-lg font-medium mb-4">{s.headline}</p>
                <p className="text-slate-400 leading-relaxed mb-6">{s.description}</p>

                <ul className="space-y-3 mb-7">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-slate-200">{b}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.id}`}
                  className="group inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-ink-950 font-bold rounded-md px-5 py-3 text-sm transition"
                >
                  Discuss this service <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <CTABand />
    </div>
  );
}
